import type { Ref } from 'vue';
import { computed } from 'vue';
import type { Field } from '../types/field';
import * as PricingTableHelper from '../helpers/pricing-table/pricing-table.helper';
import type { PricingTable } from '../types/models';
import type {
  ExtendedSummary,
  PricingTableSummaryValue,
  PricingTableSummaryValues,
  RatePricingTableSummaryValue,
} from '../..';
import {
  getSummaryValues,
  getSectionsWithRowsFromInputValues,
  getTotalDiscount,
  getTotalPriceAfterDiscount,
  getTotalPriceBeforeDiscountAndTax,
  getTotalTax,
  PricingTableSummaryHelper,
} from '../..';

export function usePricingTableSummary(pricingTable: Ref<PricingTable>, fields: Ref<Field[]>) {
  const summary = computed(() => getSummaryValues(pricingTable.value, fields.value));

  const enabledSummaries = computed<PricingTableSummaryValue[]>(() =>
    [summary.value?.discount, summary.value?.tax, summary.value?.price].filter(
      ({ enabled }) => enabled
    )
  );

  const hasValue = (value: string) => !!value && value !== '0';

  const getCurrencyFormattedValue = (value: string) =>
    PricingTableHelper.currencyFormat(value, pricingTable.value.currencySettings);

  const getFormattedExtendedSummaryValue = (summary: ExtendedSummary) => {
    const value = summary.value || '0';

    switch (summary.unit) {
      case 'Currency':
        return getCurrencyFormattedValue(value);
      case 'Percentage':
        return `${value}%`;
      default:
        return value;
    }
  };

  const totalPriceBeforeDiscountAndTax = computed(() =>
    getTotalPriceBeforeDiscountAndTax(pricingTable.value.pricingTableSections, fields.value)
  );

  const totalPriceAfterDiscount = computed(() =>
    getTotalPriceAfterDiscount(summary.value, pricingTable.value.pricingTableSections, fields.value)
  );

  const totalTaxText = computed(() =>
    hasValue(summary.value.tax.value)
      ? getTotalTax(
          summary.value,
          pricingTable.value.currencySettings,
          pricingTable.value.preCalculated,
          getSectionsWithRowsFromInputValues(pricingTable.value, fields.value),
          fields.value
        )
      : ''
  );

  const totalDiscountText = computed(() =>
    hasValue(summary.value.discount.value)
      ? getTotalDiscount(
          summary.value,
          pricingTable.value.currencySettings,
          pricingTable.value.preCalculated,
          getSectionsWithRowsFromInputValues(pricingTable.value, fields.value),
          fields.value
        )
      : ''
  );

  const baseSubtotalText = computed(() =>
    getCurrencyFormattedValue(summary.value.baseSubtotal.value)
  );

  const combinedSubTotalText = computed(() =>
    getCurrencyFormattedValue(summary.value.combinedSubtotal.value)
  );

  const taxBreakdownTotalText = computed(() =>
    getCurrencyFormattedValue(summary.value.taxBreakdown.value)
  );

  const totalPriceText = computed(() => getCurrencyFormattedValue(summary.value.price.value));

  const isSummaryEnabled = computed(() => summary.value.price.enabled);

  const taxRatesUpdated = computed(() => {
    const { toAdd, toRemove } = PricingTableSummaryHelper.getTaxRatesDifferences(
      pricingTable.value,
      fields.value
    );
    return toAdd.length > 0 || toRemove.length > 0;
  });

  const visibleTaxBreakdownRates = computed(() =>
    summary.value.taxBreakdown.rates.filter(rate => rate.enabled && rate.value !== '0')
  );

  const updateSummaryValue = <K extends keyof PricingTableSummaryValue>(
    name: keyof Omit<PricingTableSummaryValues, 'displayName'>,
    key: K,
    value: PricingTableSummaryValue[K]
  ): Required<PricingTableSummaryValues> => ({
    ...summary.value,
    [name]: {
      ...summary.value[name],
      [key]: value,
    },
  });

  const updateTaxBreakdownRate = <K extends keyof RatePricingTableSummaryValue>(
    rate: number,
    key: K,
    value: RatePricingTableSummaryValue[K]
  ): Required<PricingTableSummaryValues> => {
    const rates = summary.value.taxBreakdown.rates.map(taxRate =>
      taxRate.rate === rate ? { ...taxRate, [key]: value } : taxRate
    );
    return {
      ...summary.value,
      taxBreakdown: {
        ...summary.value.taxBreakdown,
        rates,
      },
    };
  };

  return {
    summary,
    enabledSummaries,
    getFormattedExtendedSummaryValue,
    hasValue,
    totalPriceBeforeDiscountAndTax,
    totalPriceAfterDiscount,
    totalTaxText,
    totalDiscountText,
    totalPriceText,
    baseSubtotalText,
    combinedSubTotalText,
    isSummaryEnabled,
    updateSummaryValue,
    updateTaxBreakdownRate,
    getCurrencyFormattedValue,
    taxRatesUpdated,
    taxBreakdownTotalText,
    visibleTaxBreakdownRates,
  };
}
